import { EMapProvider, IApiConfig, ICommonSettings, IDatasourceDto, IUserInfoDto } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { PureComponent, ReactNode } from "react";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import { ConfigAIInput } from "./app-settings-config-ai/app-settings-config-ai-keys-input";
import { ConfigAuthCookiesInput } from "./app-settings-config-auth-cookies/app-settings-config-auth-cookies-input";
import { ConfigExpireOldSessions } from "./app-settings-config-auth/app-settings-config-auth-expire-old-sessions";
import { ConfigTwoFa } from "./app-settings-config-auth/app-settings-config-auth-two-fa";
import { ConfigBackendUrlInput } from "./app-settings-config-backend-url/app-settings-config-backend-url-input";
import { ConfigBucketUploadInput } from "./app-settings-config-bucket-upload/app-settings-config-bucket-upload-input";
import { ConfigCacheInput } from "./app-settings-config-cache/app-settings-config-cache-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "./app-settings-config-container";
import { ConfigDateFormat } from "./app-settings-config-date-format/app-settings-config-date-format";
import { ConfigDBConnectionStringInput } from "./app-settings-config-db-connection-string/app-settings-config-db-connection-string-input";
import { ConfigDeeplinkInput } from "./app-settings-config-deeplink/app-settings-config-deeplink-input";
import { ConfigDocumentManagementInput } from "./app-settings-config-document-management/app-settings-config-document-management-input";
import { ConfigElasticApmInput } from "./app-settings-config-elastic-apm/app-settings-config-elastic-apm-input";
import { ConfigErrorMessageMode } from "./app-settings-config-error-handling/app-settings-config-error-message-mode";
import { ConfigFileUploadPathInput } from "./app-settings-config-file-upload-path/app-settings-config-file-upload-path-settings-input";
import { ConfigFormAuthInput } from "./app-settings-config-form-authentication/app-settings-config-form-authentication-input";
import { GoogleRecaptchaInput } from "./app-settings-config-google-recaptcha/app-settings-config-google-recaptcha-input";
import { InversCloudBoxxInput } from "./app-settings-config-invers-cloud-boxx/app-settings-config-invers-cloud-boxx-input";
import { ConfigOtherLoginInputs } from "./app-settings-config-login-options/app-settings-config-other-login-inputs";
import { ConfigMailInput } from "./app-settings-config-mail/app-settings-config-mail-input";
import { ConfigMapProviderSelect } from "./app-settings-config-maps/app-settings-config-map-provider-select";
import { ConfigAMapWebServiceInput } from "./app-settings-config-mcp/app-settings-config-amap-web-service-input";
import { ConfigApifyInput } from "./app-settings-config-mcp/app-settings-config-apify-input";
import { ConfigBraveSearchInput } from "./app-settings-config-mcp/app-settings-config-brave-search-input";
import { ConfigDropboxInput } from "./app-settings-config-mcp/app-settings-config-dropbox-input";
import { ConfigFirecrawlMcpInput } from "./app-settings-config-mcp/app-settings-config-firecrawl-input";
import { ConfigGithubInput } from "./app-settings-config-mcp/app-settings-config-github-input";
import { ConfigGoogleMcpInput } from "./app-settings-config-mcp/app-settings-config-google-mcp-input";
import { ConfigGoogleMcpMapInput } from "./app-settings-config-mcp/app-settings-config-google-mcp-map-input";
import { ConfigJiraInput } from "./app-settings-config-mcp/app-settings-config-jira-input";
import { ConfigOffice365McpInput } from "./app-settings-config-mcp/app-settings-config-office-365-mcp-input";
import { ConfigSlackInput } from "./app-settings-config-mcp/app-settings-config-slack-input";
import { ConfigTrelloMcpInput } from "./app-settings-config-mcp/app-settings-config-trello-input";
import { ConfigYoutubeMcpInput } from "./app-settings-config-mcp/app-settings-config-youtube-input";
import { ConfigNotifyActionDurationInput } from "./app-settings-config-notify-action/app-settings-config-notify-action-duration-input";
import { ConfigPasswordsInput } from "./app-settings-config-passwords/app-settings-config-password-input";
import { ConfigPaymentInput } from "./app-settings-config-payment-methods/app-settings-config-payment-input";
import { ConfigIpRateLimitingInput } from "./app-settings-config-rate-limit/app-settings-config-ip-rate-limiting-input";
import { ConfigRestApiInput } from "./app-settings-config-rest-api/app-settings-config-rest-api-input";
import { ConfigSapServiceBaseUrlInput } from "./app-settings-config-sap-service-base-url/app-settings-config-sap-service-base-url-input";
import { ConfigSearchEngineInput } from "./app-settings-config-search-engine/app-settings-config-search-engine-input";
import { ConfigSearchServiceInput } from "./app-settings-config-search-service/app-settings-config-search-service-input";
import { ConfigSwaggerUiInput } from "./app-settings-config-swagger-ui/app-settings-config-swagger-ui-input";
import { WhatsappBusinessInput } from "./app-settings-config-whatsapp-business/app-settings-config-whatsapp-business-input";
import { APP_SETTINGS_CATEGORIES, IConfigCategory, visibleCategories } from "./config-categories";
import { ConfigField, ConfigSection } from "./config-pane-parts";
import {
  ConfigCategoryStatus,
  ConfigDifference,
  ConfigPaneShell,
  ConfigRestorableValue,
  getOverriddenCategoryIds,
  readCategoryInheritance,
  restoreCategory
} from "./config-pane-shell";

export interface IConfigAppSettingsProps {
  /**
   * The container's whole parent/child difference, not just this pane's slice: a
   * category can own a key that lives on another one, and Restore writes it by its
   * full path. The rows below read `parentChildDifferences`, which narrows it.
   */
  parentChildDifference?: ConfigDifference;
  /** Form paths edited since the configuration was opened — the rail's dots and the orange labels. */
  changedPaths?: ReadonlySet<string>;
  /** Puts the named paths back to what they were when the configuration was opened. */
  onRevertPaths?: (paths: string[]) => void;
  /** True only when this configuration inherits from a workspace one. */
  hasParentConfig?: boolean;
  isProjectMobile?: boolean;
  isGlobalConfig?: boolean;
  commonSettings: ICommonSettings;
  datasources: IDatasourceDto[];
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput: (formItemName: string[], value: ConfigRestorableValue) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
  userInfo?: IUserInfoDto;
}

export interface IConfigAppSettingsState {
  /** The rail's selection. Pane-local, so it survives a trip through another platform tab. */
  activeCategoryId: string;
}

interface IAppSettingsSection {
  description?: ReactNode;
  titleTestId?: string;
  descriptionTestId?: string;
  body: ReactNode;
}

/**
 * The App Settings pane — the backend half of a configuration, and by a distance the
 * largest: 23 categories across five groups.
 */
export class AppSettings extends PureComponent<IConfigAppSettingsProps, IConfigAppSettingsState> {
  constructor(props: IConfigAppSettingsProps) {
    super(props);
    this.state = { activeCategoryId: APP_SETTINGS_CATEGORIES[0].id };
  }

  /**
   * The categories this pane is drawing.
   *
   * A workspace configuration answers none of the questions that are a project's to answer:
   * where its own backend and files live, what it caches with, where its APM reports. A
   * deeplink is a mobile project's, and Kuika Cloud only appears on a plan that has it.
   * The rail is built from the same booleans that gate the sections, so it can never offer
   * a category the body does not have.
   */
  getCategories = (): IConfigCategory[] => {
    const hidden: string[] = [];
    if (this.props.isGlobalConfig || !this.props.userInfo?.currentPlanName) hidden.push("kuikaCloud");
    if (this.props.isGlobalConfig) hidden.push("cacheSettings", "elasticApm");
    if (this.props.isGlobalConfig || !this.props.isProjectMobile) hidden.push("deeplinkSetting");
    if (KMainFunctions.isMainAI()) hidden.push("ai");
    return visibleCategories(APP_SETTINGS_CATEGORIES, hidden);
  };

  getActiveCategory = (): IConfigCategory => {
    const categories: IConfigCategory[] = this.getCategories();
    const selected: IConfigCategory | undefined = categories.find(
      (category: IConfigCategory): boolean => category.id === this.state.activeCategoryId
    );
    return selected ?? categories[0];
  };

  handleSelectCategory = (categoryId: string): void => {
    this.setState({ activeCategoryId: categoryId });
  };

  /**
   * This pane's own slice of the difference — what the per-row restore affordances read.
   * A row only ever overrides a key on the slice it writes to.
   */
  get parentChildDifferences(): IApiConfig | undefined {
    return this.props.parentChildDifference?.apiConfig as IApiConfig | undefined;
  }

  handleRestoreCategory = (): void => {
    restoreCategory(
      this.getActiveCategory(),
      this.props.parentChildDifference,
      "apiConfig",
      this.props.handleUpdateCustomFormInput
    );
  };

  renderConnectionSettings = (): ReactNode => (
    <>
      <ConfigDBConnectionStringInput
        parentConfigValue={this.parentChildDifferences?.dbConnectionStringSettingsId}
        commonSettings={this.props.commonSettings}
        getSectionDescription={this.props.getSectionDescription}
        handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
        handleCreateCommonConfig={this.props.handleCreateCommonConfig}
        handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
        handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
      />
      {/* Where the generated app reaches ITS backend and ITS files: a project's answer,
          not a workspace default. */}
      {!this.props.isGlobalConfig && (
        <>
          <ConfigBackendUrlInput
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />
          <ConfigFileUploadPathInput
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />
          <ConfigSapServiceBaseUrlInput
            parentConfigValue={this.parentChildDifferences?.sapServiceBaseUrlSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />
        </>
      )}
    </>
  );

  renderAuthentication = (): ReactNode => (
    <>
      <ConfigFormAuthInput
        parentConfigValue={this.parentChildDifferences?.authProviderSettingsId}
        commonSettings={this.props.commonSettings}
        getSectionDescription={this.props.getSectionDescription}
        handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
        handleCreateCommonConfig={this.props.handleCreateCommonConfig}
        handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
        handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
      />
      <ConfigPasswordsInput
        parentConfigValue={this.parentChildDifferences?.passwordPoliciesId}
        commonSettings={this.props.commonSettings}
        getSectionDescription={this.props.getSectionDescription}
        handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
        handleCreateCommonConfig={this.props.handleCreateCommonConfig}
        handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
        handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
      />
      <ConfigOtherLoginInputs
        parentConfigGoogleAuthValue={this.parentChildDifferences?.googleAuthId}
        parentConfigMsalAuthValue={this.parentChildDifferences?.msalAuthId}
        parentConfigOtpAuthValue={this.parentChildDifferences?.otpSettingsId}
        commonSettings={this.props.commonSettings}
        getSectionDescription={this.props.getSectionDescription}
        handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
        handleCreateCommonConfig={this.props.handleCreateCommonConfig}
        handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
        handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
      />
      {/* Session lifetime is a project's policy; the workspace has no sessions of its own. */}
      {!this.props.isGlobalConfig && (
        <>
          <ConfigTwoFa />
          <ConfigExpireOldSessions />
        </>
      )}
    </>
  );

  getSections = (): Record<string, IAppSettingsSection> => ({
    kuikaCloud: {
      description: this.props.getSectionDescription("appsettings_kuikacloud_description"),
      titleTestId: "appSettingsKuikaCloudTitle",
      descriptionTestId: "appSettingsKuikaCloudDescription",
      // Hosting is managed by Kuika on this plan; there is nothing to configure, only to say.
      body: undefined
    },
    connectionSettings: {
      description: this.props.getSectionDescription("appsettings_connectionsettings_description"),
      titleTestId: "appSettingsConnectionSettingsTitle",
      descriptionTestId: "appSettingsConnectionSettingsDescription",
      body: this.renderConnectionSettings()
    },
    datasourceConfigurations: {
      description:
        this.props.getSectionDescription("appsettings_restapi_configurations_description") ??
        "Assign a connection configuration to each REST, Swagger or OData datasource. Configurations are shared across the project — create one once and reuse it across datasources.",
      titleTestId: "appSettingsRestApiConfigurationsTitle",
      descriptionTestId: "appSettingsRestApiConfigurationsDescription",
      body: (
        <ConfigRestApiInput
          parentConfigValue={this.parentChildDifferences?.restApiDatasourceBindings}
          commonSettings={this.props.commonSettings}
          datasources={this.props.datasources}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    rateLimiting: {
      description: this.props.getSectionDescription("appsettings_ratelimiting_description"),
      body: (
        <ConfigIpRateLimitingInput
          parentConfigValue={this.parentChildDifferences?.ipRateLimitingSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    documentManagement: {
      description: this.props.getSectionDescription("appsettings_documentmanagement_description"),
      body: (
        <ConfigDocumentManagementInput
          parentConfigValue={this.parentChildDifferences?.documentManagementSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    fullTextSearchEngine: {
      description: this.props.getSectionDescription("appsettings_searchengine_description"),
      body: (
        <ConfigSearchEngineInput
          parentConfigValue={this.parentChildDifferences?.searchEngineSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    bucketUpload: {
      description: this.props.getSectionDescription(""),
      body: (
        <ConfigBucketUploadInput
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    cacheSettings: {
      description: this.props.getSectionDescription("appsettings_cachesettings_description"),
      body: (
        <ConfigCacheInput
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    searchService: {
      description: this.props.getSectionDescription("appsettings_searchservice_description"),
      titleTestId: "appSettingsSearchServiceTitle",
      descriptionTestId: "appSettingsSearchServiceDescription",
      body: (
        <ConfigSearchServiceInput
          parentConfigValue={this.parentChildDifferences?.searchServiceSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    authentication: {
      description: this.props.getSectionDescription("appsettings_authentication_authorization_description"),
      titleTestId: "appSettingsAuthTitle",
      descriptionTestId: "appSettingsAuthDescription",
      body: this.renderAuthentication()
    },
    authenticationCookies: {
      description: this.props.getSectionDescription("appsettings_authcookies_description"),
      titleTestId: "appSettingsAuthCookiesTitle",
      descriptionTestId: "appSettingsAuthCookiesDescription",
      body: <ConfigAuthCookiesInput />
    },
    mail: {
      description: this.props.getSectionDescription("appsettings_mail_description"),
      titleTestId: "appSettingsMailTitle",
      descriptionTestId: "appSettingsMailDescription",
      body: (
        <ConfigMailInput
          parentConfigValue={this.parentChildDifferences?.mailSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    ai: {
      description: this.props.getSectionDescription("appsettings_ai_description"),
      body: (
        <ConfigAIInput
          parentConfigOpenAIValue={this.parentChildDifferences?.openAISettingsId}
          parentConfigAzureOpenAIValue={this.parentChildDifferences?.azureOpenAISettingsId}
          parentConfigGeminiValue={this.parentChildDifferences?.geminiSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    paymentMethod: {
      description: this.props.getSectionDescription("appsettings_paymentmethod_description"),
      titleTestId: "appSettingsPaymentMethodTitle",
      descriptionTestId: "appSettingsPaymentMethodDescription",
      body: (
        <ConfigPaymentInput
          parentConfigStripeValue={this.parentChildDifferences?.stripeSettingsId}
          parentConfigIyzicoValue={this.parentChildDifferences?.iyzicoSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    whatsappBusiness: {
      description: this.props.getSectionDescription("appsettings_whatsappbusiness_description"),
      body: (
        <WhatsappBusinessInput
          parentConfigValue={this.parentChildDifferences?.whatsappBusinessSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    inversCloudBoxx: {
      description: this.props.getSectionDescription("appsettings_inverscloudboxx_description"),
      body: (
        <InversCloudBoxxInput
          parentConfigValue={this.parentChildDifferences?.inversCloudBoxxSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    googleRecaptcha: {
      description: this.props.getSectionDescription("appsettings_googlerecaptcha_description"),
      body: (
        <GoogleRecaptchaInput
          parentConfigValue={this.parentChildDifferences?.googleRecaptchaSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    mcp: {
      description: this.props.getSectionDescription("appsettings_mcp_description"),
      body: (
        <>
          <ConfigBraveSearchInput
            parentConfigValue={this.parentChildDifferences?.braveSearchSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />

          <ConfigSlackInput
            parentConfigValue={this.parentChildDifferences?.slackSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />

          <ConfigDropboxInput
            parentConfigValue={this.parentChildDifferences?.dropboxSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />

          <ConfigGithubInput
            parentConfigValue={this.parentChildDifferences?.githubSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />

          <ConfigGoogleMcpInput
            parentConfigValue={this.parentChildDifferences?.googleMcpSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />

          <ConfigMapProviderSelect
            parentData="apiConfig"
            label="Maps provider"
            tooltipContentKey="appsettings_mcp_map_provider"
            renderProvider={(provider) =>
              provider === EMapProvider.Amap ? (
                <ConfigAMapWebServiceInput
                  parentConfigValue={this.parentChildDifferences?.amapWebServiceSettingsId}
                  commonSettings={this.props.commonSettings}
                  getSectionDescription={this.props.getSectionDescription}
                  handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
                  handleCreateCommonConfig={this.props.handleCreateCommonConfig}
                  handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
                  handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
                />
              ) : (
                <ConfigGoogleMcpMapInput
                  parentConfigValue={this.parentChildDifferences?.googleMapMcpSettingsId}
                  commonSettings={this.props.commonSettings}
                  getSectionDescription={this.props.getSectionDescription}
                  handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
                  handleCreateCommonConfig={this.props.handleCreateCommonConfig}
                  handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
                  handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
                />
              )
            }
          />

          <ConfigJiraInput
            parentConfigValue={this.parentChildDifferences?.jiraMcpSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />

          <ConfigYoutubeMcpInput
            parentConfigValue={this.parentChildDifferences?.youtubeMcpSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />

          <ConfigTrelloMcpInput
            parentConfigValue={this.parentChildDifferences?.trelloMcpSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />

          <ConfigOffice365McpInput
            parentConfigValue={this.parentChildDifferences?.office365McpSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />

          <ConfigFirecrawlMcpInput
            parentConfigValue={this.parentChildDifferences?.firecrawlMcpSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />

          <ConfigApifyInput
            parentConfigValue={this.parentChildDifferences?.apifySettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />
        </>
      )
    },
    swaggerUi: {
      description: this.props.getSectionDescription("appsettings_swaggerui_description"),
      titleTestId: "appSettingsSwaggerUiTitle",
      descriptionTestId: "appSettingsSwaggerUiDescription",
      body: (
        <>
          <ConfigField label="Use Swagger UI">
            <Form.Item name={["apiConfig", "useSwaggerUi"]} valuePropName="checked" noStyle>
              <KuikaSwitch dataTestId="appSettingsSwaggerUiSwitch" />
            </Form.Item>
          </ConfigField>
          <ConfigSwaggerUiInput />
        </>
      )
    },
    elasticApm: {
      description: this.props.getSectionDescription("appsettings_elasticapm_description"),
      body: (
        <ConfigElasticApmInput
          parentConfigValue={this.parentChildDifferences?.elasticApmSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    notifyActions: {
      description: this.props.getSectionDescription("appsettings_notifyactions_description"),
      titleTestId: "appSettingsNotifyActionsTitle",
      descriptionTestId: "appSettingsNotifyActionsDescription",
      body: (
        <ConfigNotifyActionDurationInput
          parentConfigValue={this.parentChildDifferences?.notifyActionsDefaultDurationSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    errorMessages: {
      description: this.props.getSectionDescription("appsettings_errormessages_description"),
      titleTestId: "appSettingsErrorMessagesTitle",
      descriptionTestId: "appSettingsErrorMessagesDescription",
      body: <ConfigErrorMessageMode parentData="apiConfig" />
    },
    deeplinkSetting: {
      description: this.props.getSectionDescription("appsettings_deeplinksetting_description"),
      titleTestId: "appSettingsDeeplinkSettingTitle",
      descriptionTestId: "appSettingsDeeplinkSettingDescription",
      body: (
        <ConfigDeeplinkInput
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    dateTimeFormats: {
      description: this.props.getSectionDescription("appsettings_date_timeformats_description"),
      // `webConfig` is not a mistake: the formats are stored on the web configuration and
      // applied across every target the project builds.
      body: <ConfigDateFormat parentData="webConfig" />
    }
  });

  /**
   * App Settings has no platform switch and no icons — what belongs beside it is the copy
   * for whichever category is open, which is otherwise a paragraph the eye skips over the
   * top of a long form.
   */
  renderAside = (): ReactNode => {
    const active: IConfigCategory = this.getActiveCategory();
    const section: IAppSettingsSection | undefined = this.getSections()[active.id];

    return (
      <>
        {this.props.hasParentConfig && (
          <ConfigCategoryStatus
            inheritance={readCategoryInheritance(active, this.props.parentChildDifference, "apiConfig")}
            onRestore={this.handleRestoreCategory}
          />
        )}

        <ConfigSection rail title="Description" titleTestId="appSettingsDescriptionTitle">
          <Form.Item name="description">
            <KuikaInput
              data-testid="appSettingsDescriptionInput"
              size="md"
              type="textarea"
              rows={5}
              resizable={false}
              placeholder="Any note about your configuration"
            />
          </Form.Item>
        </ConfigSection>

        {/* The selected category's own copy. It used to sit above the fields, where a
            23-section scroll trained the eye to skip it. */}
        {section?.description && (
          <ConfigSection rail title="About" description={section.description} descriptionTestId="configAboutSection" />
        )}
      </>
    );
  };

  render() {
    const categories: IConfigCategory[] = this.getCategories();
    const sections: Record<string, IAppSettingsSection> = this.getSections();
    const activeCategoryId: string = this.getActiveCategory().id;

    return (
      <ConfigPaneShell
        changedPaths={this.props.changedPaths}
        onRevertPaths={this.props.onRevertPaths}
        categories={categories}
        activeCategoryId={activeCategoryId}
        onSelectCategory={this.handleSelectCategory}
        overriddenCategoryIds={getOverriddenCategoryIds(categories, this.props.parentChildDifference, "apiConfig")}
        aside={this.renderAside()}
      >
        {/* Every category stays mounted; `ConfigSection` hides the ones the rail is not
            pointing at. One antd Form spans all of them and save reads `getFieldsValue()`,
            so a category taken out of the tree is a field written back empty. */}
        {categories.map((category: IConfigCategory) => (
          <ConfigSection
            key={category.id}
            categoryId={category.id}
            active={category.id === activeCategoryId}
            title={category.label}
            description={sections[category.id]?.description}
            titleTestId={sections[category.id]?.titleTestId}
            descriptionTestId={sections[category.id]?.descriptionTestId}
          >
            {sections[category.id]?.body}
          </ConfigSection>
        ))}
      </ConfigPaneShell>
    );
  }
}
